import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, FlaskConical, ArrowUpRight, Filter, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
    adminExperimentsList,
    adminExperimentsOverview,
} from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PALETTE = {
    draft: "bg-[var(--bl-stone-soft)] text-bl",
    running: "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]",
    paused: "bg-[var(--bl-amber-tint)] text-[var(--bl-amber)]",
    completed: "bg-[var(--bl-teal-tint)] text-bl-primary",
    archived: "bg-[var(--bl-stone-soft)] text-bl-muted",
};

function Stat({ label, value, accent }) {
    return (
        <div className="bl-card p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                {label}
            </div>
            <div
                className={`mt-1.5 font-display font-semibold text-3xl tnum ${
                    accent || "text-bl"
                }`}
            >
                {value ?? "—"}
            </div>
        </div>
    );
}

export default function AdminExperiments() {
    const [overview, setOverview] = useState(null);
    const [experiments, setExperiments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [scanBusy, setScanBusy] = useState(false);

    const reload = async () => {
        try {
            const [o, l] = await Promise.all([
                adminExperimentsOverview(),
                adminExperimentsList(filterStatus ? { status: filterStatus } : {}),
            ]);
            setOverview(o);
            setExperiments(l.experiments || []);
        } catch {
            toast.error("Could not load experiments.");
        } finally {
            setLoading(false);
        }
    };

    const runWinnerScan = async () => {
        setScanBusy(true);
        try {
            // Step 1: dry-run preview
            const dr = await axios.post(
                `${API}/admin/experiments/winner-scan`,
                { dry_run: true },
                { withCredentials: true },
            );
            const candidates = dr.data?.candidates || [];
            if (candidates.length === 0) {
                toast.info(
                    `Scanned ${dr.data.scanned} running experiments — no winners ready yet.`,
                );
                return;
            }
            const list = candidates
                .map(
                    (c) =>
                        `${c.experiment_name}: ${c.winner_variant_name} (+${c.lift_pct}%, ${c.confidence_level}%)`,
                )
                .join("\n");
            if (
                !window.confirm(
                    `Promote these ${candidates.length} winner(s)?\n\n${list}`,
                )
            )
                return;
            // Step 2: live promote
            const live = await axios.post(
                `${API}/admin/experiments/winner-scan`,
                { dry_run: false },
                { withCredentials: true },
            );
            toast.success(
                `Promoted ${live.data.promoted_count} winner(s). Admins emailed.`,
            );
            await reload();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Winner scan failed.",
            );
        } finally {
            setScanBusy(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [o, l] = await Promise.all([
                    adminExperimentsOverview(),
                    adminExperimentsList(
                        filterStatus ? { status: filterStatus } : {},
                    ),
                ]);
                if (!cancelled) {
                    setOverview(o);
                    setExperiments(l.experiments || []);
                }
            } catch (e) {
                toast.error("Could not load experiments.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [filterStatus]);

    return (
        <PublicLayout>
            <section
                className="bl-section py-10 sm:py-14"
                data-testid="admin-experiments-page"
            >
                <div className="bl-container">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
                        <div className="flex items-center gap-2.5">
                            <FlaskConical className="h-5 w-5 text-bl-primary" />
                            <h1 className="font-display font-semibold text-3xl tracking-tight text-bl">
                                A/B Experiments
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={runWinnerScan}
                                disabled={scanBusy}
                                className="bl-btn-primary text-xs gap-1"
                                title="Promote any running experiment that has ≥30 samples + ≥95% confidence + positive lift"
                                data-testid="admin-exp-run-winner-scan"
                            >
                                {scanBusy ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <Sparkles className="h-3 w-3" />
                                )}
                                Run winner scan
                            </button>
                            <Link
                                to="/admin"
                                className="text-sm text-bl-muted hover:text-bl underline-offset-4 hover:underline"
                            >
                                ← Back to admin
                            </Link>
                        </div>
                    </div>

                    {loading && (
                        <div
                            className="flex items-center gap-2 text-sm text-bl-muted"
                            data-testid="admin-experiments-loading"
                        >
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                        </div>
                    )}

                    {!loading && (
                        <>
                            {/* Auto-promote ROI badge — shows whether the
                                A/B → ship loop is actually firing. */}
                            {overview?.auto_promoted_30d > 0 && (
                                <div
                                    className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--bl-teal-tint)] text-bl-primary text-sm font-medium border border-bl-primary"
                                    data-testid="admin-experiments-autopromote-badge"
                                    title="The auto-promote scanner ships the winning variant once ≥30 samples + 95% confidence are reached. This count covers the last 30 days."
                                >
                                    <span className="font-mono-bl text-[11px] tracking-[0.18em] uppercase">
                                        Auto-shipped
                                    </span>
                                    <span className="font-display font-semibold text-base">
                                        {overview.auto_promoted_30d}
                                    </span>
                                    <span className="text-bl-muted text-xs">
                                        experiment{overview.auto_promoted_30d === 1 ? "" : "s"} in last 30 days
                                        {overview.auto_promoted_total > overview.auto_promoted_30d
                                            ? ` · ${overview.auto_promoted_total} all-time`
                                            : ""}
                                    </span>
                                </div>
                            )}

                            {/* KPI cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="admin-experiments-kpis">
                                <Stat label="Active" value={overview?.active} />
                                <Stat
                                    label="Completed"
                                    value={overview?.completed}
                                />
                                <Stat
                                    label="With winner"
                                    value={overview?.with_winner}
                                    accent="text-[var(--bl-moss)]"
                                />
                                <Stat
                                    label="Total visitors"
                                    value={overview?.total_visitors}
                                />
                                <Stat
                                    label="Attributed rev (USD)"
                                    value={overview?.attributed_revenue_usd?.toLocaleString?.() || "0"}
                                />
                            </div>

                            {/* Filter */}
                            <div className="flex items-center gap-2 mb-4">
                                <Filter className="h-4 w-4 text-bl-muted" />
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="bl-input text-sm py-1.5"
                                    data-testid="admin-experiments-filter"
                                >
                                    <option value="">All statuses</option>
                                    <option value="running">Running</option>
                                    <option value="paused">Paused</option>
                                    <option value="completed">Completed</option>
                                    <option value="draft">Draft</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>

                            {/* Table */}
                            <div className="bl-card overflow-x-auto" data-testid="admin-experiments-table">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr>
                                            {[
                                                "Experiment",
                                                "Location",
                                                "Variants",
                                                "Primary metric",
                                                "Visitors",
                                                "Status",
                                                "",
                                            ].map((h) => (
                                                <th
                                                    key={h}
                                                    className="px-5 py-3 text-left text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                                                >
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {experiments.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={7}
                                                    className="px-5 py-8 text-center text-bl-muted"
                                                >
                                                    No experiments yet.
                                                </td>
                                            </tr>
                                        )}
                                        {experiments.map((e) => (
                                            <tr
                                                key={e.experiment_id}
                                                className="border-b border-bl/40 last:border-b-0"
                                                data-testid={`admin-exp-row-${e.experiment_id}`}
                                            >
                                                <td className="px-5 py-3 text-bl">
                                                    <div className="font-medium">
                                                        {e.name}
                                                    </div>
                                                    {e.hypothesis && (
                                                        <div className="text-xs text-bl-muted mt-0.5 line-clamp-1">
                                                            {e.hypothesis}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-bl-muted text-xs font-mono">
                                                    {e.location}
                                                </td>
                                                <td className="px-5 py-3 text-bl text-xs font-mono">
                                                    {(e.variants || []).length}
                                                </td>
                                                <td className="px-5 py-3 text-bl-muted text-xs">
                                                    {e.primary_metric}
                                                </td>
                                                <td className="px-5 py-3 text-bl font-mono">
                                                    {e.visitors}
                                                </td>
                                                <td className="px-5 py-3">
                                                    <div className="flex flex-wrap items-center gap-1">
                                                        <span
                                                            className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded ${
                                                                STATUS_PALETTE[e.status] || STATUS_PALETTE.draft
                                                            }`}
                                                        >
                                                            {e.status}
                                                        </span>
                                                        {e.auto_promoted && (
                                                            <span
                                                                className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
                                                                title="Auto-promoted by winner scan"
                                                                data-testid={`auto-promoted-badge-${e.experiment_id}`}
                                                            >
                                                                ★ Auto-winner
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3 text-right">
                                                    <Link
                                                        to={`/admin/experiments/${e.experiment_id}`}
                                                        className="inline-flex items-center gap-1 text-sm text-bl-primary hover:underline underline-offset-4"
                                                        data-testid={`admin-exp-link-${e.experiment_id}`}
                                                    >
                                                        Details
                                                        <ArrowUpRight className="h-3.5 w-3.5" />
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
